import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "../../context/AuthContext";
import { usePlans } from "../../context/PlanContext";
import CountCard from "./CountCard";
import api from "../../services/api";

// Unified data layer is now managed dynamically.
const ALL_TOPICS = ["Marketing", "Sales", "Legal", "Operations", "Finance", "Infrastructure"];

const formatDateStr = (dateString: string | null) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const getISODate = (dateString: string | null) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function CourseLibrary() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<any[]>([]);
  const [teaserCourses, setTeaserCourses] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ completedCount: 0, inProgressCount: 0 });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [lockedTierClick, setLockedTierClick] = useState<string | null>(null);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery !== debouncedSearchQuery) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1); // reset to page 1 on new search
        setCourses([]); // Clear current courses to show fresh results
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, debouncedSearchQuery]);

  const [selectedTopics, setSelectedTopics] = useState<string[]>(searchParams.getAll("topic"));
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get("category"));
  const [selectedDate, setSelectedDate] = useState<string | null>(searchParams.get("date"));
  const [selectedTier, setSelectedTier] = useState<string | null>(searchParams.get("tier"));
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(searchParams.has("topic") || searchParams.has("category") || searchParams.has("date"));

  // Sync external URL changes (e.g. Sidebar clicks) back into local state
  useEffect(() => {
    const urlTier = searchParams.get("tier");
    if (urlTier !== selectedTier) {
      setSelectedTier(urlTier);
      setCurrentPage(1);
      setCourses([]);
    }
    // We can also sync others if needed, but tier is the main one affected by Sidebar
    setSelectedCategory(searchParams.get("category"));
    setSelectedDate(searchParams.get("date"));
    setSelectedTopics(searchParams.getAll("topic"));
  }, [searchParams, selectedTier]);

  const [ALL_CATEGORIES, setALL_CATEGORIES] = useState<string[]>([]);

  const { data: session }: any = useSession();
  const { plans, getPlanByName } = usePlans();
  const userId = session?.user?.id;
  
  const userMembership = session?.user?.highest_plan_name || session?.user?.membership || "";
  let userHighestTier = session?.user?.highest_tier_level;
  if (userHighestTier === undefined) {
    const userPlan = getPlanByName(userMembership);
    userHighestTier = userPlan ? userPlan.tier_level : 0;
  }

  // Fetch categories independently
  useEffect(() => {
    api.get('/categories')
      .then(res => {
        if (Array.isArray(res.data)) {
          setALL_CATEGORIES(res.data.map((c: any) => c.name));
        }
      })
      .catch(err => console.error("Failed to fetch categories:", err));
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.append("page", currentPage.toString());
        if (debouncedSearchQuery) params.append("search", debouncedSearchQuery);
        if (selectedCategory) params.append("category", selectedCategory);
        if (selectedDate) params.append("date", selectedDate);
        if (selectedTier) params.append("tier", selectedTier);
        if (selectedTopics.length > 0) params.append("topic", selectedTopics.join(","));

        const [cRes, pRes] = await Promise.all([
          api.get(`/courses?${params.toString()}`),
          userId ? api.get(`/progress/${userId}`) : Promise.resolve({ data: {} })
        ]);
        
        const rawData = cRes.data;
        const watchLogs = pRes.data;

        // Calculate global stats from watch logs instead of current page
        const cCount = Object.values(watchLogs).filter((p: any) => {
          const val = typeof p === 'object' && p !== null ? p.progress || 0 : Number(p) || 0;
          return val >= 95;
        }).length;
        const iPCount = Object.values(watchLogs).filter((p: any) => {
          const val = typeof p === 'object' && p !== null ? p.progress || 0 : Number(p) || 0;
          return val > 0 && val < 95;
        }).length;
        setStats({ completedCount: cCount, inProgressCount: iPCount });

        const rawCourses = rawData.data || [];
        setHasNextPage(!!rawData.next_page_url);
        
        const mapCourse = (c: any) => {
          let progressVal = 0;
          if (watchLogs[c.id]) {
            progressVal = typeof watchLogs[c.id] === 'object' ? (watchLogs[c.id].progress || 0) : Number(watchLogs[c.id]);
          }

          let safeDate = "Recently";
          const dateStr = c.created_at || c.createdAt;
          if (dateStr) {
            const parsed = new Date(dateStr);
            if (!isNaN(parsed.getTime())) {
              safeDate = parsed.toLocaleDateString();
            }
          }
          return {
            id: c.id,
            title: c.title,
            description: c.description || c.short_description || "No description provided.",
            duration: c.estimated_duration_minutes || null,
            tasksCount: c.tasks?.length || 0,
            createdAt: safeDate,
            rawDate: dateStr,
            category: c.category?.name || null,
            topics: c.topics?.map((t: any) => t.name) || [],
            difficulty: c.difficulty_level || "beginner",
            creator: c.creator?.name || "The Curator",
            progress: progressVal,
            is_locked: c.is_locked || false,
            upsell_offer: c.upsell_offer || null
          };
        };
        
        if (rawData.teaser_courses) {
          setTeaserCourses(rawData.teaser_courses.map(mapCourse));
        } else if (currentPage === 1) {
          setTeaserCourses([]);
        }

        const loadedCourses = rawCourses.map(mapCourse);

        if (currentPage === 1) {
          setCourses(loadedCourses);
        } else {
          setCourses(prev => {
             // Prevent duplicates by checking IDs
             const existingIds = new Set(prev.map(p => p.id));
             const uniqueNew = loadedCourses.filter((c: any) => !existingIds.has(c.id));
             return [...prev, ...uniqueNew];
          });
        }
      } catch (err) {
        console.error("Failed to fetch content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [debouncedSearchQuery, currentPage, selectedTopics, selectedCategory, selectedDate, selectedTier, userId]);

  const toggleTopic = (topic: string) => {
    setSelectedTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]);
    setCurrentPage(1);
    setCourses([]);
  };

  const setCategoryFilter = (cat: string | null) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    setCourses([]);
    const newParams = new URLSearchParams(searchParams);
    if (cat) {
      newParams.set("category", cat);
    } else {
      newParams.delete("category");
    }
    setSearchParams(newParams, { replace: true });
  };

  const setDateFilter = (dateStr: string | null) => {
    setSelectedDate(dateStr);
    setCurrentPage(1);
    setCourses([]);
    const newParams = new URLSearchParams(searchParams);
    if (dateStr) {
      newParams.set("date", dateStr);
    } else {
      newParams.delete("date");
    }
    setSearchParams(newParams, { replace: true });
  };

  const setTierFilter = (tierVal: string | null) => {
    setSelectedTier(tierVal);
    setCurrentPage(1);
    setCourses([]);
    const newParams = new URLSearchParams(searchParams);
    if (tierVal) {
      newParams.set("tier", tierVal);
    } else {
      newParams.delete("tier");
    }
    setSearchParams(newParams, { replace: true });
  };

  const clearAllFilters = () => {
    setSelectedTopics([]);
    setSelectedCategory(null);
    setSelectedDate(null);
    setSelectedTier(null);
    setSearchQuery("");
    setCurrentPage(1);
    setCourses([]);
    setSearchParams({}, { replace: true });
  };

  const displayedCourses = courses;

  const AVAILABLE_TIERS = [
    { label: "Basic Courses", value: "0" },
    ...plans.sort((a, b) => a.tier_level - b.tier_level).map(plan => ({
      label: `${plan.name} Courses`,
      value: plan.tier_level.toString()
    }))
  ];

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-on-surface tracking-tighter">Course Library</h2>
          <p className="text-slate-500 font-medium">Explore lessons to help you grow your business.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          <CountCard 
            title="Completed" 
            count={stats.completedCount} 
            label="Courses" 
            icon="task_alt" 
            colorClass="text-emerald-600"
          />
          <CountCard 
            title="In-Progress" 
            count={stats.inProgressCount} 
            label="Active" 
            icon="pending" 
            colorClass="text-blue-600"
          />
        </div>
      </div>

      {/* Dynamic Tier Filter */}
      {AVAILABLE_TIERS.length > 1 && (
        <div className="flex flex-wrap gap-2 p-2 bg-surface-container-low rounded-2xl w-fit border border-outline-variant/10 shadow-inner">
          <button
            onClick={() => setTierFilter(null)}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm tracking-wide transition-all ${!selectedTier ? 'bg-primary text-surface shadow-md scale-105' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-200'}`}
          >
            All Access
          </button>
          {AVAILABLE_TIERS.filter(tier => parseInt(tier.value) <= userHighestTier).map(tier => {
            const isLocked = parseInt(tier.value) > userHighestTier;
            return (
              <button
                key={tier.value}
                onClick={() => {
                  if (isLocked) {
                    setLockedTierClick(tier.label.split(" ")[0]);
                  } else {
                    setTierFilter(tier.value);
                  }
                }}
                className={`px-6 py-2.5 rounded-xl font-bold text-sm tracking-wide transition-all flex items-center gap-2 ${selectedTier === tier.value ? 'bg-primary text-surface shadow-md scale-105' : isLocked ? 'text-slate-400 opacity-70 hover:opacity-100 hover:bg-slate-100' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-200'}`}
              >
                {isLocked && <span className="material-symbols-outlined text-[14px]">lock</span>}
                {tier.label}
                {!isLocked && selectedTier === tier.value && <span className="material-symbols-outlined text-[16px]">check_circle</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="space-y-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">search</span>
            <input 
              type="text" 
              placeholder="Search by your business problem, goal, or dream"
              className="w-full pl-12 pr-4 py-3 bg-surface-container-lowest text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button 
            onClick={() => setIsAdvancedSearch(!isAdvancedSearch)}
            className={`px-6 py-3 rounded-xl font-bold text-xs flex items-center gap-2 transition-all border ${
              isAdvancedSearch ? "bg-primary text-white border-primary" : "bg-surface-container-lowest text-on-surface border-outline-variant/10 hover:brightness-95"
            }`}
          >
            <span className="material-symbols-outlined text-sm">tune</span>
            Advanced
          </button>
        </div>

        {/* Advanced Topic Filters */}
        {isAdvancedSearch && (
          <div className="pt-4 border-t border-dashed border-outline-variant/10 animate-in fade-in slide-in-from-top-2 duration-300 space-y-6">
            
            <div>
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Filter by Publish Date</p>
              <div className="relative max-w-xs cursor-pointer group">
                <input 
                  type="date" 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  value={getISODate(selectedDate)}
                  onClick={(e) => {
                    try {
                      if ('showPicker' in HTMLInputElement.prototype) {
                        (e.target as HTMLInputElement).showPicker();
                      }
                    } catch (err) {}
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {
                      setDateFilter(null);
                    } else {
                      const [y, m, d] = val.split('-');
                      const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                      const parts = dateObj.toDateString().split(' ');
                      setDateFilter(`${parts[1]}-${parts[2]}-${parts[3]}`); // e.g. Jul-04-2026
                    }
                  }}
                />
                <div className="w-full pl-12 pr-4 py-2 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl group-focus-within:ring-2 group-focus-within:ring-primary/20 transition-all text-sm font-medium flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-slate-400 absolute left-4">calendar_month</span>
                    <span className="pl-1 py-0.5">{selectedDate ? formatDateStr(selectedDate) : "Select date..."}</span>
                  </div>
                  <span className="material-symbols-outlined text-slate-400 text-sm group-hover:text-primary transition-colors">edit_calendar</span>
                </div>
              </div>
            </div>

            {ALL_CATEGORIES.length > 0 && (
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Filter by Category</p>
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => setCategoryFilter(null)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all border ${
                      !selectedCategory 
                      ? "bg-primary/10 text-primary border-primary/20" 
                      : "bg-surface-container-high text-slate-500 border-transparent hover:bg-slate-200 dark:hover:bg-slate-800"
                    }`}
                  >
                    All Categories
                  </button>
                  {ALL_CATEGORIES.map(cat => (
                    <button 
                      key={cat}
                      onClick={() => setCategoryFilter(cat as string)}
                      className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all border ${
                        selectedCategory === cat 
                        ? "bg-primary/10 text-primary border-primary/20" 
                        : "bg-surface-container-high text-slate-500 border-transparent hover:bg-slate-200 dark:hover:bg-slate-800"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Filter by Topics</p>
              <div className="flex flex-wrap gap-2">
                {ALL_TOPICS.map(topic => (
                  <button 
                    key={topic}
                    onClick={() => toggleTopic(topic)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all border ${
                      selectedTopics.includes(topic) 
                      ? "bg-primary/10 text-primary border-primary/20" 
                      : "bg-surface-container-high text-slate-500 border-transparent hover:bg-slate-200 dark:hover:bg-slate-800"
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>

            {(selectedTopics.length > 0 || selectedCategory || selectedDate || searchQuery) && (
              <div className="pt-2">
                <button 
                  onClick={clearAllFilters}
                  className="px-4 py-1.5 rounded-full text-xs font-bold text-red-500 hover:bg-red-50 transition-colors inline-flex items-center gap-1 border border-red-500/20"
                >
                  <span className="material-symbols-outlined text-[14px]">close</span>
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Teaser Tuesdays (Ghost Upsell passive discovery) */}
      {teaserCourses.length > 0 && !debouncedSearchQuery && (
        <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-[2.5rem] p-8 space-y-6">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-amber-500 text-3xl">magic_button</span>
            <div>
              <h3 className="text-xl font-black text-amber-600 dark:text-amber-400 tracking-tighter">Teaser Tuesday Unlocks ✨</h3>
              <p className="text-sm font-bold text-slate-500">A sneak peek at premium content just for today.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teaserCourses.map((course, idx) => (
              <CourseCard key={`teaser-${course.id}`} course={course} idx={idx} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* WhatsApp Upsell Modal */}
      {lockedTierClick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            onClick={() => setLockedTierClick(null)}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
          />
          <div className="relative w-full max-w-md bg-surface border border-outline-variant/10 rounded-[2.5rem] p-8 shadow-2xl z-50 flex flex-col gap-6 animate-in zoom-in-95 duration-300">
              <div className="text-center space-y-4">
                <div className="inline-flex p-4 rounded-3xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-4xl font-black">rocket_launch</span>
                </div>
                <h3 className="text-xl md:text-2xl font-black tracking-tighter text-on-surface leading-tight">
                  🚀 Make Marketing Easy with AI
                </h3>
                <div className="text-sm font-semibold text-slate-500 space-y-3">
                  <p>
                    Learn how to use ChatGPT & AI Assistants for marketing — from content and business strategies to customer nurturing, WhatsApp Automation & Meta Ads.
                  </p>
                  <p className="text-primary font-bold">
                    Upgrade to GROWTH Membership for practical LIVE AI training + LIVE Q&A.
                  </p>
                </div>
              </div>
            <div className="pt-2 flex flex-col gap-3">
              <a
                href={`https://wa.me/${session?.user?.support_whatsapp ? session.user.support_whatsapp.replace(/\+/g, '') : '919074509112'}?text=${encodeURIComponent(`Hi, I want to upgrade to ${lockedTierClick}!`)}`}
                target="_blank"
                rel="noreferrer"
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all text-center flex items-center justify-center gap-2"
              >
                Chat on WhatsApp
                <span className="material-symbols-outlined text-sm">chat</span>
              </a>
              <button
                type="button"
                onClick={() => setLockedTierClick(null)}
                className="w-full py-3 text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Course Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-on-surface">
          {displayedCourses.length > 0 && displayedCourses.map((course, idx) => (
            <CourseCard key={course.id} course={course} idx={idx} navigate={navigate} />
          ))}
          
          {/* Loading Skeleton */}
          {isLoading && [1, 2, 3, 4, 5, 6].map(i => (
            <div key={`skeleton-${i}`} className="animate-pulse bg-surface-container-lowest border border-outline-variant/10 rounded-[2rem] p-6 flex flex-col h-full min-h-[320px]">
              {/* Tags Skeleton */}
              <div className="flex gap-1.5 mb-4">
                <div className="h-4 w-16 bg-surface-container-high rounded-[4px]"></div>
                <div className="h-4 w-12 bg-surface-container-high rounded-[4px]"></div>
              </div>
              
              {/* Title Skeleton */}
              <div className="h-7 w-3/4 bg-surface-container-high rounded mb-4"></div>
              
              {/* Meta Info Skeleton */}
              <div className="h-3 w-1/2 bg-surface-container-high rounded mb-5"></div>
              
              {/* Description Skeleton */}
              <div className="space-y-2.5 mb-6 flex-1 mt-2">
                <div className="h-3 w-full bg-surface-container-high rounded"></div>
                <div className="h-3 w-5/6 bg-surface-container-high rounded"></div>
              </div>
              
              {/* Creator Skeleton */}
              <div className="h-3 w-1/4 bg-surface-container-high rounded mb-6"></div>
              
              {/* Progress Bar Area Skeleton */}
              <div className="space-y-3 pt-6 border-t border-outline-variant/5">
                <div className="flex justify-between">
                  <div className="h-3 w-1/4 bg-surface-container-high rounded"></div>
                  <div className="h-3 w-8 bg-surface-container-high rounded"></div>
                </div>
                <div className="h-2 w-full bg-surface-container-high rounded-full"></div>
              </div>
            </div>
          ))}

          {displayedCourses.length === 0 && !isLoading && (
            <div className="col-span-full py-20 text-center space-y-4">
              <span className="material-symbols-outlined text-6xl text-slate-200">search_off</span>
              <p className="text-slate-400 font-medium italic">No courses found matching your current filters.</p>
            </div>
          )}
        </div>
        {/* Pagination: Load More */}
        {hasNextPage && (
          <div className="flex justify-center items-center gap-4 pt-10">
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={isLoading}
              className="px-6 py-3.5 bg-surface-container-high rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-surface-container-highest transition-colors hover:scale-105 shadow-sm hover:shadow-md disabled:opacity-50 disabled:hover:scale-100"
            >
              {isLoading ? 'Loading...' : 'Load More Courses'}
              <span className="material-symbols-outlined text-sm">expand_more</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CourseCard({ course, idx, navigate }: { course: any, idx: number, navigate: any }) {
  return (
    <div 
      id={idx === 0 ? "tour-first-course" : undefined}
      data-course-id={course.id}
      onClick={() => navigate(`/courses/${course.id}`)}
      className={`group bg-surface-container-lowest border border-outline-variant/10 rounded-[2rem] p-6 hover:shadow-2xl hover:translate-y-[-4px] transition-all cursor-pointer flex flex-col h-full relative overflow-hidden ${course.is_locked ? 'opacity-90' : ''}`}
    >
      {course.is_locked && (
        <div className="absolute inset-0 z-10 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center">
          <div className="bg-white/90 dark:bg-slate-800/90 p-4 rounded-full shadow-2xl backdrop-blur-md text-slate-800 dark:text-white flex flex-col items-center justify-center border border-white/20">
            <span className="material-symbols-outlined text-4xl">lock</span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <span className={`text-[8px] font-black px-2 py-0.5 rounded-[4px] uppercase tracking-widest border flex items-center gap-1 w-fit ${
          course.difficulty === 'advanced' ? 'border-rose-200 bg-rose-50 text-rose-700' :
          course.difficulty === 'intermediate' ? 'border-amber-200 bg-amber-50 text-amber-700' :
          'border-slate-800 text-slate-900 bg-transparent'
        }`}>
          <span className="material-symbols-outlined text-[10px]">
            {course.difficulty === 'advanced' ? 'local_fire_department' : 
             course.difficulty === 'intermediate' ? 'trending_up' : 
             'radio_button_unchecked'}
          </span>
          {course.difficulty === 'advanced' ? 'ADVANCED' : course.difficulty === 'intermediate' ? 'INTERMEDIATE' : 'BEGINNER'}
        </span>
        {course.topics.map((t: string) => (
          <span key={t} className="text-[8px] font-black bg-slate-100 px-2 py-0.5 rounded-[4px] uppercase tracking-widest text-slate-900 border border-slate-200/50">
            {t}
          </span>
        ))}
        <span className="text-[8px] font-black bg-blue-100 text-blue-600 px-2 py-0.5 rounded uppercase tracking-widest border border-blue-200">New</span>
      </div>
      
      <h4 className="text-xl font-black text-on-surface mb-2 group-hover:text-primary transition-colors tracking-tighter">{course.title}</h4>
      
      <div className="flex flex-wrap items-center gap-3 mb-4 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
        {course.duration && (
          <span className="flex items-center gap-1 text-slate-500">
            <span className="material-symbols-outlined text-[12px]">schedule</span> {course.duration} Mins
          </span>
        )}
        {course.duration && <span className="opacity-30">•</span>}
        <span className="flex items-center gap-1 text-slate-500">
          <span className="material-symbols-outlined text-[12px]">task</span> {course.tasksCount || 0} Task{course.tasksCount !== 1 ? 's' : ''}
        </span>
        
      </div>
      <p 
        className="text-xs text-slate-500 leading-relaxed font-medium mb-6 flex-1 line-clamp-2 overflow-hidden"
        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
      >
        {course.description}
      </p>

      <div className="flex items-center gap-2 mb-6 text-[10px] font-bold text-slate-400">
        <span className="material-symbols-outlined text-sm">person</span>
        <span>By {course.creator}</span>
      </div>

      <div className="space-y-3 pt-6 border-t border-outline-variant/5">
        <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
          <span className={course.progress === 0 ? "text-primary" : course.progress < 100 ? "text-amber-500" : "text-slate-400"}>
            {course.progress === 0 ? "Start Mission" : course.progress < 100 ? "🔥 Next Mission" : "🎉 Mission Complete!"}
          </span>
          <span className={course.progress === 100 ? "text-emerald-500" : ""}>
            {course.progress === 100 ? "Verified" : `${course.progress}%`}
          </span>
        </div>
        <div className="h-2 w-full bg-surface-container-high rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-1000 ${course.progress === 100 ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]" : "bg-primary"}`} 
            style={{ width: `${course.progress}%` }} 
          />
        </div>
      </div>
    </div>
  );
}
